import { useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';

export type SearchableSelectOption = { value: string; label: string; group?: string };

/**
 * Type-to-filter combobox styled like the app's text inputs. The selected
 * option's label shows in the input; focusing clears it for typing, closing
 * without a pick restores it. Arrow up/down + Enter + Escape keyboard
 * navigation, click to select, ✕ clears, closes on outside click.
 */
export function SearchableSelect({ options, value, onChange, placeholder, required, disabled, emptyMessage, className, wrapperClassName, clearable = true, onQueryChange }: {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Shown instead of "No matches" (e.g. while options load). */
  emptyMessage?: string;
  /** Overrides the input classes (e.g. FilterBar's bg-bg-panel variant). */
  className?: string;
  /** Extra classes on the root div (e.g. width in a filter row). */
  wrapperClassName?: string;
  /** Show the ✕ clear button when a value is set. Default true. */
  clearable?: boolean;
  /** Live query text while the dropdown is open ('' on focus) — lets parents
   * drive server-side search instead of shipping the whole option list. */
  onQueryChange?: (query: string) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const selected = options.find((o) => o.value === value) ?? null;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    listRef.current?.children[Math.min(active, listRef.current.children.length - 1)]
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  function pick(option: SearchableSelectOption) {
    onChange(option.value);
    setOpen(false);
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setQuery('');
        setActive(0);
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (filtered.length === 0 ? 0 : (i + 1) % filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (filtered.length === 0 ? 0 : (i - 1 + filtered.length) % filtered.length));
    } else if (e.key === 'Enter') {
      if (filtered[active]) {
        e.preventDefault();
        pick(filtered[active]);
      }
    } else if (e.key === 'Escape') {
      // Keep the event from reaching modal-level Escape handlers (useEscape).
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  }

  let lastGroup: string | undefined;
  let idx = -1;

  return (
    <div ref={rootRef} className={clsx('relative', wrapperClassName)}>
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded={open}
        aria-required={required}
        disabled={disabled}
        value={open ? query : (selected?.label ?? '')}
        placeholder={placeholder}
        onFocus={() => { setQuery(''); setActive(0); setOpen(true); onQueryChange?.(''); }}
        onChange={(e) => { setQuery(e.target.value); setActive(0); setOpen(true); onQueryChange?.(e.target.value); }}
        onKeyDown={onKeyDown}
        className={clsx(
          className ?? 'w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent disabled:opacity-50',
          selected && clearable && !open && 'pr-8',
        )}
      />
      {selected && clearable && !disabled && (
        <button
          type="button"
          aria-label="Clear selection"
          onClick={() => { onChange(''); setQuery(''); inputRef.current?.focus(); }}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-white text-sm"
        >
          ✕
        </button>
      )}
      {open && (
        <div ref={listRef} className="absolute z-10 mt-1 w-full bg-bg-panel border border-border rounded-lg shadow-2xl max-h-56 overflow-y-auto animate-pop">
          {filtered.length === 0 && (
            <p className="px-3 py-3 text-sm text-muted">{emptyMessage ?? 'No matches.'}</p>
          )}
          {filtered.map((o) => {
            idx += 1;
            const i = idx;
            const header = o.group !== lastGroup && o.group ? (
              <div key={`g-${o.group}`} className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted">{o.group}</div>
            ) : null;
            lastGroup = o.group;
            return (
              <div key={o.value}>
                {header}
                <button
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o)}
                  className={clsx(
                    'w-full px-3 py-2 text-left text-sm truncate',
                    i === active ? 'bg-bg-hover text-white' : 'text-white',
                    o.value === value && 'text-accent',
                  )}
                >
                  {o.label}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
